import { memo, useCallback } from 'react';

import { type UseFieldArrayRemove, useFormContext } from 'react-hook-form';

import { IconButton } from '@pulse-web-ui/button-v2';
import type { DaDataPartySuggestion } from '@pulse-web-ui/dadata';
import { Delete } from '@pulse-web-ui/icons';
import { useTheme } from '@pulse-web-ui/theme';

import {
  ColumnContainer,
  DaDataPartySuggestionController,
  RowContainer,
} from '@shared/ui';
import { addTestAttribute } from '@shared/utils';

import { useTranslation } from '../../../helpers';
import { InputContainer } from './legal-entity-field.styles';
import type { LegalEntitiesFormValues } from './types';

interface LegalEntityFormProps {
  index: number;
  isDisabled?: boolean;
  onRemove?: UseFieldArrayRemove;
}

export const LegalEntityField = memo(
  ({ index, isDisabled, onRemove }: LegalEntityFormProps) => {
    const theme = useTheme();
    const t = useTranslation();

    const {
      control,
      formState: { errors },
      clearErrors,
      setValue,
    } = useFormContext<LegalEntitiesFormValues>();

    const hanldeOnRemove = useCallback(
      (index: number) => () => {
        onRemove?.(index);
      },
      [onRemove]
    );

    const handleLegalEntityNameChange = useCallback(
      (suggestionValue?: DaDataPartySuggestion) => {
        const inn = suggestionValue?.data.inn;
        if (inn) {
          setValue(`legalEntities.${index}.legalEntityInn`, inn);
        }

        if (errors?.legalEntities?.[index]?.legalEntityName) {
          clearErrors(`legalEntities.${index}.legalEntityName`);
        }
      },
      [errors, index]
    );

    return (
      <ColumnContainer $gap={16}>
        <RowContainer
          $gap={8}
          $marginBottom={0}
          $align='flex-start'
        >
          <InputContainer>
            <DaDataPartySuggestionController
              control={control}
              name={`legalEntities.${index}.legalEntityName`}
              uid={'legalEntityName'}
              label={t('legalEntityFounderStatusForm.labels.legalEntityName')}
              onChange={handleLegalEntityNameChange}
              testId={`legalEntities-${index}-legalEntityName`}
            />
          </InputContainer>
          <IconButton
            onClick={hanldeOnRemove(index)}
            variant='secondary'
            size='l'
            disabled={isDisabled}
            icon={
              <Delete
                width={24}
                color={theme.colors.icon.primary}
              />
            }
            {...addTestAttribute('remove-legal-entity')}
          />
        </RowContainer>
      </ColumnContainer>
    );
  }
);
